import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createTextVNode as _createTextVNode, unref as _unref, toDisplayString as _toDisplayString, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, renderList as _renderList, vModelSelect as _vModelSelect, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = ["data-theme"]
const _hoisted_2 = { class: "container py-3 d-flex align-items-center justify-content-between" }
const _hoisted_3 = { class: "d-none d-lg-flex align-items-center gap-3" }
const _hoisted_4 = {
  key: 0,
  class: "dropdown-menu dropdown-menu-end show mt-1",
  style: {"display":"block","position":"absolute","right":"0","top":"100%","min-width":"240px"}
}
const _hoisted_5 = { class: "dropdown-item d-flex align-items-center justify-content-between gap-2" }
const _hoisted_6 = { class: "form-check form-switch m-0" }
const _hoisted_7 = ["checked"]
const _hoisted_8 = { class: "px-3 py-2" }
const _hoisted_9 = ["value"]
const _hoisted_10 = ["href"]
const _hoisted_11 = {
  key: 0,
  class: "bi bi-moon-stars"
}
const _hoisted_12 = {
  key: 1,
  class: "bi bi-sun"
}
const _hoisted_13 = ["aria-expanded"]
const _hoisted_14 = {
  key: 0,
  id: "mobile-menu",
  class: "container d-lg-none mb-3"
}
const _hoisted_15 = { class: "card p-3 gap-2" }
const _hoisted_16 = {
  key: 1,
  class: "d-grid gap-2"
}
const _hoisted_17 = { class: "d-flex align-items-center justify-content-between mt-2 small text-muted-token" }
const _hoisted_18 = { class: "d-flex align-items-center gap-2" }
const _hoisted_19 = {
  key: 0,
  class: "bi bi-moon-stars"
}
const _hoisted_20 = {
  key: 1,
  class: "bi bi-sun"
}
const _hoisted_21 = { class: "form-check form-switch m-0" }
const _hoisted_22 = ["checked"]
const _hoisted_23 = {
  class: "toast-container position-fixed top-0 end-0 p-3",
  style: {"z-index":"9999"}
}
const _hoisted_24 = { class: "d-flex" }
const _hoisted_25 = { class: "toast-body" }
const _hoisted_26 = ["onClick"]

import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useToastStore } from './stores/toast';
import { appKitReady } from './lib/appkit';
import { useWalletStore } from './stores/wallet';
import { ThemeController } from '@reown/appkit-controllers';
import { useChainStore } from './stores/chain';
// Prefer saved theme; fall back to system preference
const TESTNET_ID = 11155111; // Sepolia

export default /*@__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props) {

const getPreferredTheme = () => {
  const saved = localStorage.getItem('theme');
  if (saved === 'light' || saved === 'dark') return saved;
  const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  return prefersDark ? 'dark' : 'light';
};
const theme = ref(getPreferredTheme());
const toggleTheme = () => {
  theme.value = theme.value === 'dark' ? 'light' : 'dark';
  localStorage.setItem('theme', theme.value);
};
const toast = useToastStore();
const toasts = computed(() => toast.items);
const removeToast = (id: number) => toast.remove(id);
const isAppKit = computed(() => appKitReady());
const wallet = useWalletStore();
wallet.init();
const router = useRouter();
const chain = useChainStore();
chain.init();
const chains = computed(() => chain.supported);
const selectedChainId = ref<number>(chain.selectedId);
const DEFAULT_MAINNET_ID = Number((process.env.VUE_APP_DEFAULT_CHAIN_ID as string) || '1');
const isTestnet = computed(() => chain.selectedId === TESTNET_ID);
const lastMainnetId = ref<number>(isTestnet.value ? DEFAULT_MAINNET_ID : chain.selectedId);
const lastTestnetId = ref<number>(isTestnet.value ? chain.selectedId : TESTNET_ID);
async function onChainChange() {
  chain.select(selectedChainId.value);
  try {
    await wallet.ensureChain(chain.current.chain);
  } catch (_) {
    // ignore; user may reject network switch
  }
}
async function setMode(toTestnet: boolean) {
  const targetId = toTestnet ? (lastTestnetId.value || TESTNET_ID) : (lastMainnetId.value || DEFAULT_MAINNET_ID);
  selectedChainId.value = targetId;
  await onChainChange();
}
async function onModeSwitchChange(ev: Event) {
  const target = ev.target as HTMLInputElement;
  await setMode(!!target.checked);
}
async function connectWallet() {
  if (isAppKit.value) {
    try {
      const mod = await import('@reown/appkit/vue-core');
      const { open } = mod.useAppKit();
      await open();
      return;
    } catch (_) {
      // fall through to injected
    }
  }
  await wallet.openModal();
  if (!wallet.isConnected) {
    toast.push('error', 'Unable to open wallet modal. Set VUE_APP_WALLETCONNECT_PROJECT_ID or use injected wallet (MetaMask).');
  } else {
    toast.push('info', 'Connected with injected wallet');
  }
}

async function openWalletModal() {
  await router.push('/wallet');
  showWalletMenu.value = false;
}

async function disconnectWallet() {
  try {
    await wallet.disconnect();
    toast.push('info', 'Wallet disconnected');
  } catch (e) {
    toast.push('error', 'Failed to disconnect');
  }
}

// Ensure theme attributes are applied to :root so CSS variables and Bootstrap switch
const applyTheme = () => {
  document.documentElement.setAttribute('data-theme', theme.value);
  // Bootstrap 5.3+ color modes
  document.documentElement.setAttribute('data-bs-theme', theme.value);
  try { ThemeController.setThemeMode(theme.value as any); } catch {}
};
onMounted(applyTheme);
watch(theme, applyTheme);

// Remember last selected per mode for smoother toggling
watch(() => chain.selectedId, (id) => {
  selectedChainId.value = id;
  if (id === TESTNET_ID) lastTestnetId.value = id;
  else lastMainnetId.value = id;
});

const shortAddress = computed(() => wallet.address ? `${wallet.address.slice(0,6)}…${wallet.address.slice(-4)}` : '');
const networkName = computed(() => chain.current.label);
const chainBadgeClass = computed(() => chain.current.id === 11155111 ? 'badge-tinted-accent' : 'badge-tinted-primary');
const explorerAddress = computed(() => wallet.address ? `${chain.current.explorerBase || 'https://sepolia.etherscan.io'}/address/${wallet.address}` : '#');
const explorerLabel = computed(() => {
  const base = chain.current.explorerBase || '';
  return base.includes('etherscan') ? 'View on Etherscan' : 'View on Explorer';
});

// Wallet dropdown
const showWalletMenu = ref(false);
const walletMenuRef = ref<HTMLElement | null>(null);
const toggleWalletMenu = () => { showWalletMenu.value = !showWalletMenu.value; };
const onDocClick = (e: MouseEvent) => {
  const el = walletMenuRef.value;
  if (!el) return;
  if (!el.contains(e.target as Node)) showWalletMenu.value = false;
};
onMounted(() => document.addEventListener('click', onDocClick));
onBeforeUnmount(() => document.removeEventListener('click', onDocClick));

const onDisconnectClick = async () => {
  await disconnectWallet();
  showWalletMenu.value = false;
};
const onConnectClick = async () => {
  await connectWallet();
  showWalletMenu.value = false;
};
async function copyAddress() {
  if (!wallet.address) return;
  try {
    await navigator.clipboard.writeText(wallet.address);
    toast.push('info', 'Address copied');
  } catch {
    toast.push('error', 'Failed to copy');
  }
}

// Mobile menu
const mobileMenuOpen = ref(false);
// Close on route change
watch(() => router.currentRoute.value.fullPath, () => { mobileMenuOpen.value = false; });
// Close on upsize to lg+
function onResize() {
  if (window.innerWidth >= 992) mobileMenuOpen.value = false;
}
onMounted(() => window.addEventListener('resize', onResize));
onBeforeUnmount(() => window.removeEventListener('resize', onResize));

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!
  const _component_router_view = _resolveComponent("router-view")!

  return (_openBlock(), _createElementBlock("div", {
    class: "min-vh-100",
    "data-theme": theme.value
  }, [
    _createElementVNode("header", _hoisted_2, [
      _createVNode(_component_router_link, {
        class: "",
        to: "/"
      }, {
        default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
          _createElementVNode("div", { class: "d-flex align-items-center gap-2" }, [
            _createElementVNode("img", {
              src: "/assets/logo.svg",
              alt: "Flat 18",
              height: "46"
            })
          ], -1)
        ]))]),
        _: 1
      }),
      _createElementVNode("div", _hoisted_3, [
        _createVNode(_component_router_link, {
          class: "text-decoration-none link-body-emphasis nav-affordance",
          to: "/"
        }, {
          default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
            _createTextVNode("Home", -1)
          ]))]),
          _: 1
        }),
        _createVNode(_component_router_link, {
          class: "text-decoration-none link-body-emphasis nav-affordance",
          to: "/swap"
        }, {
          default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
            _createTextVNode("Swap", -1)
          ]))]),
          _: 1
        }),
        _createVNode(_component_router_link, {
          class: "text-decoration-none link-body-emphasis nav-affordance",
          to: "/docs"
        }, {
          default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
            _createTextVNode("Docs", -1)
          ]))]),
          _: 1
        }),
        _createVNode(_component_router_link, {
          class: "text-decoration-none link-body-emphasis nav-affordance",
          to: "/admin"
        }, {
          default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
            _createTextVNode("Admin", -1)
          ]))]),
          _: 1
        }),
        _createElementVNode("div", {
          ref_key: "walletMenuRef",
          ref: walletMenuRef,
          class: "position-relative"
        }, [
          _createElementVNode("span", {
            class: _normalizeClass(['badge', 'menu-badge', chainBadgeClass.value]),
            role: "button",
            onClick: toggleWalletMenu,
            style: {"cursor":"pointer"}
          }, [
            (_unref(wallet).address)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  _createTextVNode(_toDisplayString(networkName.value) + " · " + _toDisplayString(shortAddress.value), 1)
                ], 64))
              : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                  _createTextVNode(" Connect Wallet ")
                ], 64)),
            _createElementVNode("i", {
              class: _normalizeClass(["bi", showWalletMenu.value ? 'bi-chevron-up' : 'bi-chevron-down'])
            }, null, 2)
          ], 2),
          (showWalletMenu.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                (!_unref(wallet).isConnected)
                  ? (_openBlock(), _createElementBlock("button", {
                      key: 0,
                      class: "dropdown-item d-flex align-items-center gap-2",
                      onClick: onConnectClick
                    }, [...(_cache[11] || (_cache[11] = [
                      _createElementVNode("i", { class: "bi bi-wallet2" }, null, -1),
                      _createTextVNode(" Connect Wallet ", -1)
                    ]))]))
                  : (_openBlock(), _createElementBlock("button", {
                      key: 1,
                      class: "dropdown-item d-flex align-items-center gap-2",
                      onClick: openWalletModal
                    }, [...(_cache[12] || (_cache[12] = [
                      _createElementVNode("i", { class: "bi bi-gear" }, null, -1),
                      _createTextVNode(" Wallet Settings ", -1)
                    ]))])),
                _cache[17] || (_cache[17] = _createElementVNode("div", { class: "dropdown-divider" }, null, -1)),
                _cache[18] || (_cache[18] = _createElementVNode("div", { class: "px-3 py-2 small text-muted-token" }, "Network", -1)),
                _createElementVNode("div", _hoisted_5, [
                  _cache[13] || (_cache[13] = _createElementVNode("span", null, "Testnet mode", -1)),
                  _createElementVNode("div", _hoisted_6, [
                    _createElementVNode("input", {
                      class: "form-check-input",
                      type: "checkbox",
                      id: "menuModeSwitch",
                      checked: isTestnet.value,
                      onChange: onModeSwitchChange
                    }, null, 40, _hoisted_7)
                  ])
                ]),
                _createElementVNode("div", _hoisted_8, [
                  _withDirectives(_createElementVNode("select", {
                    class: "form-select form-select-sm",
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedChainId).value = $event)),
                    onChange: onChainChange
                  }, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(chains.value, (c) => {
                      return (_openBlock(), _createElementBlock("option", {
                        key: c.id,
                        value: c.id
                      }, _toDisplayString(c.label), 9, _hoisted_9))
                    }), 128))
                  ], 544), [
                    [
                      _vModelSelect,
                      selectedChainId.value,
                      void 0,
                      { number: true }
                    ]
                  ])
                ]),
                _cache[19] || (_cache[19] = _createElementVNode("div", { class: "dropdown-divider" }, null, -1)),
                (_unref(wallet).address)
                  ? (_openBlock(), _createElementBlock("button", {
                      key: 2,
                      class: "dropdown-item d-flex align-items-center gap-2",
                      onClick: copyAddress
                    }, [...(_cache[14] || (_cache[14] = [
                      _createElementVNode("i", { class: "bi bi-clipboard" }, null, -1),
                      _createTextVNode(" Copy Address ", -1)
                    ]))]))
                  : _createCommentVNode("", true),
                (_unref(wallet).address)
                  ? (_openBlock(), _createElementBlock("a", {
                      key: 3,
                      class: "dropdown-item d-flex align-items-center gap-2",
                      href: explorerAddress.value,
                      target: "_blank",
                      rel: "noopener"
                    }, [
                      _cache[15] || (_cache[15] = _createElementVNode("i", { class: "bi bi-box-arrow-up-right" }, null, -1)),
                      _createTextVNode(" " + _toDisplayString(explorerLabel.value), 1)
                    ], 8, _hoisted_10))
                  : _createCommentVNode("", true),
                (_unref(wallet).address)
                  ? (_openBlock(), _createElementBlock("button", {
                      key: 4,
                      class: "dropdown-item text-danger d-flex align-items-center gap-2",
                      onClick: onDisconnectClick
                    }, [...(_cache[16] || (_cache[16] = [
                      _createElementVNode("i", { class: "bi bi-x-circle" }, null, -1),
                      _createTextVNode(" Disconnect ", -1)
                    ]))]))
                  : _createCommentVNode("", true)
              ]))
            : _createCommentVNode("", true)
        ], 512),
        _createElementVNode("button", {
          class: "badge btn btn-sm btn-primary",
          onClick: toggleTheme,
          "aria-label": "Toggle color theme"
        }, [
          (theme.value==='light')
            ? (_openBlock(), _createElementBlock("i", _hoisted_11))
            : (_openBlock(), _createElementBlock("i", _hoisted_12))
        ])
      ]),
      _createElementVNode("button", {
        class: "btn btn-outline-secondary d-flex d-lg-none align-items-center justify-content-center",
        type: "button",
        "aria-label": "Toggle navigation",
        "aria-expanded": mobileMenuOpen.value ? 'true' : 'false',
        "aria-controls": "mobile-menu",
        onClick: _cache[1] || (_cache[1] = ($event: any) => (mobileMenuOpen.value = !mobileMenuOpen.value))
      }, [
        _createElementVNode("i", {
          class: _normalizeClass(["bi", mobileMenuOpen.value ? 'bi-x' : 'bi-list']),
          style: {"font-size":"1.25rem"}
        }, null, 2)
      ], 8, _hoisted_13)
    ]),
    (mobileMenuOpen.value)
      ? (_openBlock(), _createElementBlock("nav", _hoisted_14, [
          _createElementVNode("div", _hoisted_15, [
            _createVNode(_component_router_link, {
              class: "text-decoration-none py-2",
              to: "/",
              onClick: _cache[2] || (_cache[2] = ($event: any) => (mobileMenuOpen.value=false))
            }, {
              default: _withCtx(() => [...(_cache[20] || (_cache[20] = [
                _createTextVNode("Home", -1)
              ]))]),
              _: 1
            }),
            _createVNode(_component_router_link, {
              class: "text-decoration-none py-2",
              to: "/swap",
              onClick: _cache[3] || (_cache[3] = ($event: any) => (mobileMenuOpen.value=false))
            }, {
              default: _withCtx(() => [...(_cache[21] || (_cache[21] = [
                _createTextVNode("Swap", -1)
              ]))]),
              _: 1
            }),
            _createVNode(_component_router_link, {
              class: "text-decoration-none py-2",
              to: "/docs",
              onClick: _cache[4] || (_cache[4] = ($event: any) => (mobileMenuOpen.value=false))
            }, {
              default: _withCtx(() => [...(_cache[22] || (_cache[22] = [
                _createTextVNode("Docs", -1)
              ]))]),
              _: 1
            }),
            _createVNode(_component_router_link, {
              class: "text-decoration-none py-2",
              to: "/admin",
              onClick: _cache[5] || (_cache[5] = ($event: any) => (mobileMenuOpen.value=false))
            }, {
              default: _withCtx(() => [...(_cache[23] || (_cache[23] = [
                _createTextVNode("Admin", -1)
              ]))]),
              _: 1
            }),
            _cache[29] || (_cache[29] = _createElementVNode("div", { class: "border-top my-2" }, null, -1)),
            (!_unref(wallet).isConnected)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 0,
                  class: "btn btn-primary w-100",
                  onClick: onConnectClick
                }, [...(_cache[24] || (_cache[24] = [
                  _createElementVNode("i", { class: "bi bi-wallet2 me-1" }, null, -1),
                  _createTextVNode(" Connect Wallet ", -1)
                ]))]))
              : (_openBlock(), _createElementBlock("div", _hoisted_16, [
                  _createElementVNode("button", {
                    class: "btn btn-outline-secondary w-100",
                    onClick: openWalletModal
                  }, [...(_cache[25] || (_cache[25] = [
                    _createElementVNode("i", { class: "bi bi-gear me-1" }, null, -1),
                    _createTextVNode(" Wallet Settings ", -1)
                  ]))]),
                  _createElementVNode("button", {
                    class: "btn btn-outline-danger w-100",
                    onClick: onDisconnectClick
                  }, [...(_cache[26] || (_cache[26] = [
                    _createElementVNode("i", { class: "bi bi-x-circle me-1" }, null, -1),
                    _createTextVNode(" Disconnect ", -1)
                  ]))])
                ])),
            _createElementVNode("div", _hoisted_17, [
              _createElementVNode("div", _hoisted_18, [
                _cache[27] || (_cache[27] = _createElementVNode("span", null, "Theme", -1)),
                _createElementVNode("button", {
                  class: "btn btn-sm btn-outline-secondary",
                  onClick: toggleTheme,
                  "aria-label": "Toggle color theme"
                }, [
                  (theme.value==='light')
                    ? (_openBlock(), _createElementBlock("i", _hoisted_19))
                    : (_openBlock(), _createElementBlock("i", _hoisted_20))
                ])
              ]),
              _createElementVNode("div", _hoisted_21, [
                _createElementVNode("input", {
                  class: "form-check-input",
                  type: "checkbox",
                  id: "menuModeSwitchMobile",
                  checked: isTestnet.value,
                  onChange: onModeSwitchChange
                }, null, 40, _hoisted_22),
                _cache[28] || (_cache[28] = _createElementVNode("label", {
                  class: "form-check-label",
                  for: "menuModeSwitchMobile"
                }, "Testnet", -1))
              ])
            ])
          ])
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("main", null, [
      _createVNode(_component_router_view)
    ]),
    _cache[30] || (_cache[30] = _createElementVNode("footer", { class: "container py-4 text-secondary small" }, " For demonstration only. Not financial advice. Non‑custodial; you retain full control of your wallet. Network fees may apply. Always verify contract addresses. ", -1)),
    _createElementVNode("div", _hoisted_23, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(toasts.value, (t) => {
        return (_openBlock(), _createElementBlock("div", {
          key: t.id,
          class: _normalizeClass(["toast show align-items-center text-bg-success border-0 mb-2", { 'text-bg-danger': t.type==='error', 'text-bg-info': t.type==='info' }])
        }, [
          _createElementVNode("div", _hoisted_24, [
            _createElementVNode("div", _hoisted_25, _toDisplayString(t.message), 1),
            _createElementVNode("button", {
              type: "button",
              class: "btn-close btn-close-white me-2 m-auto",
              onClick: ($event: any) => (removeToast(t.id))
            }, null, 8, _hoisted_26)
          ])
        ], 2))
      }), 128))
    ])
  ], 8, _hoisted_1))
}
}

})